import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, FieldArray, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import PageHeader from '../../components/Shared/PageHeader';
import SharedButton from '../../components/Shared/SharedButton';
import SharedInputText from '../../components/Shared/SharedInputText';
import SharedDropdown from '../../components/Shared/SharedDropdown';
import { DeleteIcon } from '../../components/Shared/TableActionIcons';
import { getERPClient } from '../../services/supplyChainApi';
import ERPItemSearchField from './ERPItemSearchField';

const createInvoiceNumber = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `PINV-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
};

const createLine = () => ({
  item_id: '',
  item_code: '',
  item_name: '',
  quantity: 1,
  uom_id: '',
  unit_price: 0,
  discount1_pct: 0,
  discount2_pct: 0,
  vat_enabled: true,
  vat_pct: 15,
  purchase_price: 0,
  bonus_qty: 0,
  batch_number: '',
  expiry_date: '',
  line_total: 0,
});

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const normalizeDate = (v) => {
  if (!v) return '';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const calculateInvoiceLine = (line) => {
  const quantity = toNumber(line.quantity);
  const unitPrice = toNumber(line.unit_price);
  const discount1Pct = toNumber(line.discount1_pct);
  const discount2Pct = toNumber(line.discount2_pct);
  const vatPct = line.vat_enabled === false ? 0 : (toNumber(line.vat_pct) > 0 ? toNumber(line.vat_pct) : 15);

  const base = quantity * unitPrice;
  const discount1 = base * (discount1Pct / 100);
  const afterDiscount1 = base - discount1;
  const discount2 = afterDiscount1 * (discount2Pct / 100);
  const netBeforeVat = afterDiscount1 - discount2;
  const vatAmount = netBeforeVat * (vatPct / 100);
  const lineTotal = netBeforeVat + vatAmount;
  const purchasePrice = unitPrice * (1 - discount1Pct / 100) * (1 - discount2Pct / 100);

  return { base, discount1, discount2, vatAmount, lineTotal, purchasePrice };
};

const toDateInput = (v) => {
  if (!v) return '';
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

const getItemId = (item) => item?.id || item?.item_id || item?.value || '';

const buildLevelOptions = (levels) =>
  (Array.isArray(levels) ? levels : [])
    .filter((l) => l.is_active !== false && (l.uom_id || l.uom?.id))
    .map((l) => ({
      value: l.uom_id || l.uom?.id,
      label: `${l.uom_code || l.uom?.code || ''} (× ${l.factor_to_base ?? 1})`,
    }));

// On edit, load each saved line item's packaging levels so the correct UOM
// options are populated and the stored UOM is restored.
function RestoreItemUOMs({ existing, onLevels }) {
  const { setFieldValue } = useFormikContext();
  const onLevelsRef = React.useRef(onLevels);
  useEffect(() => {
    onLevelsRef.current = onLevels;
  }, [onLevels]);

  useEffect(() => {
    if (!existing || !Array.isArray(existing.lines)) return;
    let cancelled = false;
    (async () => {
      const erpClient = getERPClient();
      for (let idx = 0; idx < existing.lines.length; idx++) {
        const line = existing.lines[idx];
        const itemId = line.item_id || line.item?.id;
        if (!itemId) continue;
        try {
          const res = await erpClient.getItemPackagingLevels(itemId);
          const levels = Array.isArray(res) ? res : (res?.data || []);
          if (cancelled) return;
          onLevelsRef.current(itemId, buildLevelOptions(levels));
          const active = levels.filter((l) => l.is_active !== false);
          const currentUom = line.uom_id || line.uom?.id || '';
          const chosen = currentUom
            ? active.find((l) => String(l.uom_id || l.uom?.id) === String(currentUom)) || null
            : active.find((l) => l.is_default_purchase) || active.find((l) => l.is_base_uom) || active[0] || null;
          if (chosen?.uom_id) setFieldValue(`lines.${idx}.uom_id`, chosen.uom_id, false);
          else if (currentUom) setFieldValue(`lines.${idx}.uom_id`, currentUom, false);
        } catch (err) {
          console.error('Failed to load item packaging levels:', err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [existing, setFieldValue]);

  return null;
}

function InvoiceTotalsSync({ values, setFieldValue }) {
  useEffect(() => {
    const computed = values.lines.map(calculateInvoiceLine);
    const subtotal = Number(computed.reduce((s, l) => s + l.base, 0).toFixed(2));
    const discountTotal = Number(computed.reduce((s, l) => s + l.discount1 + l.discount2, 0).toFixed(2));
    const vatTotal = Number(computed.reduce((s, l) => s + l.vatAmount, 0).toFixed(2));
    const grandTotal = Number(computed.reduce((s, l) => s + l.lineTotal, 0).toFixed(2));

    if (Number(values.subtotal || 0) !== subtotal) setFieldValue('subtotal', subtotal, false);
    if (Number(values.discount_total || 0) !== discountTotal) setFieldValue('discount_total', discountTotal, false);
    if (Number(values.vat_total || 0) !== vatTotal) setFieldValue('vat_total', vatTotal, false);
    if (Number(values.grand_total || 0) !== grandTotal) setFieldValue('grand_total', grandTotal, false);
  }, [setFieldValue, values.lines, values.subtotal, values.discount_total, values.vat_total, values.grand_total]);

  return null;
}

export default function CreateERPPurchaseInvoicePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [preOrders, setPreOrders] = useState([]);
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [itemUomOptions, setItemUomOptions] = useState({});

  const setItemLevels = useCallback((itemId, options) => {
    setItemUomOptions((prev) => ({ ...prev, [itemId]: options }));
  }, []);

  useEffect(() => {
    async function loadMasterData() {
      try {
        const erpClient = getERPClient();
        const [supRes, itemRes, uomRes, poRes] = await Promise.allSettled([
          erpClient.getSuppliers({ limit: 500 }),
          erpClient.getItems({ limit: 1000, is_active: true }),
          erpClient.getUoms(),
          erpClient.getPreOrders({ limit: 200, offset: 0 }),
        ]);
        if (supRes.status === 'fulfilled' && supRes.value) {
          const list = Array.isArray(supRes.value) ? supRes.value : (supRes.value.data || []);
          setSuppliers(list);
        }
        if (itemRes.status === 'fulfilled' && itemRes.value) {
          const list = Array.isArray(itemRes.value) ? itemRes.value : (itemRes.value.data || []);
          setItems(list);
        }
        if (uomRes.status === 'fulfilled' && uomRes.value) {
          const list = Array.isArray(uomRes.value) ? uomRes.value : (uomRes.value.data || []);
          setUoms(list);
        }
        if (poRes.status === 'fulfilled' && poRes.value) {
          const list = Array.isArray(poRes.value) ? poRes.value : (poRes.value.data || []);
          setPreOrders(list);
        }
      } catch (err) {
        console.error('Failed to load invoice master data:', err);
      }
    }
    loadMasterData();
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      setLoading(true);
      try {
        const erpClient = getERPClient();
        const res = await erpClient.getPurchaseInvoice(id);
        const inv = res.data || res;
        if (inv && (inv.id || inv.invoice_number)) {
          setExisting(inv);
        }
      } catch (err) {
        console.error('Failed to load purchase invoice:', err);
        toast.error(t('Failed to load purchase invoice'));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  const uomOptions = (uoms || []).map((u) => ({
    value: u.id,
    label: u.code ? `${u.code} - ${u.name}`.replace(/^- /, '') : (u.id || ''),
  }));

  const supplierOptions = (suppliers || []).map((row) => ({
    value: row.id,
    label: row.legal_name_en || row.name_en || row.name_ar || row.id,
  }));

  const preOrderOptions = [
    { value: 'none', label: t('No Purchase Order') },
    ...(preOrders || []).map((row) => ({
      value: row.id,
      label: row.pre_order_number || row.id,
    })),
  ];

  const initialValues = useMemo(() => {
    const ex = existing || {};
    const rawLines = (Array.isArray(ex.lines) && ex.lines.length > 0 ? ex.lines : []);
    return {
      invoice_number: ex.invoice_number || createInvoiceNumber(),
      supplier_id: ex.supplier_id || '',
      pre_order_id: ex.pre_order_id || 'none',
      invoice_date: toDateInput(ex.invoice_date) || new Date().toISOString().slice(0, 10),
      payment_due_date: toDateInput(ex.payment_due_date) || new Date().toISOString().slice(0, 10),
      payment_type: ex.payload?.payment_type || 'CREDIT',
      currency: ex.currency || 'SAR',
      subtotal: ex.subtotal || 0,
      discount_total: ex.discount_total || 0,
      vat_total: ex.vat_total || 0,
      grand_total: ex.total_amount || ex.grand_total || 0,
      status: ex.status || 'RECEIVED',
      payment_terms: ex.payment_terms || '90 DAYS',
      lines: rawLines.length
        ? rawLines.map((line) => {
          const vPct = toNumber(line.vat_pct ?? line.vat_rate ?? 15);
          const vEnabled = typeof line.vat_enabled === 'boolean' ? line.vat_enabled : vPct > 0;
          return {
            ...createLine(),
            ...line,
            item_id: line.item_id || line.item?.id || '',
            item_code: line.item_code || line.item?.item_code || '',
            item_name: line.item_name || line.item?.name_en || line.item?.name_ar || '',
            uom_id: line.uom_id || line.uom?.id || '',
            quantity: toNumber(line.quantity ?? line.qty ?? 1),
            unit_price: toNumber(line.unit_price || 0),
            discount1_pct: toNumber(line.discount1_pct ?? 0),
            discount2_pct: toNumber(line.discount2_pct ?? 0),
            vat_enabled: vEnabled,
            vat_pct: vEnabled ? (vPct > 0 ? vPct : 15) : 0,
            purchase_price: toNumber(line.purchase_price ?? line.unit_price ?? 0),
            bonus_qty: toNumber(line.bonus_qty ?? 0),
            batch_number: line.batch_number || '',
            expiry_date: normalizeDate(line.expiry_date),
          };
        })
        : [createLine()],
    };
  }, [existing]);

  const validationSchema = Yup.object({
    invoice_number: Yup.string().required(t('Invoice number is required')),
    supplier_id: Yup.string().required(t('Supplier is required')),
    pre_order_id: Yup.string().nullable(),
    invoice_date: Yup.date().required(t('Invoice date is required')),
    payment_due_date: Yup.date()
      .required(t('Payment due date is required'))
      .min(Yup.ref('invoice_date'), t('Payment due date must be on or after invoice date')),
    payment_type: Yup.string().required(t('Payment type is required')),
    currency: Yup.string().length(3).required(t('Currency is required')),
    status: isEdit ? Yup.string().nullable() : Yup.string().required(t('Status is required')),
    lines: Yup.array()
      .of(
        Yup.object({
          item_id: Yup.string().required(t('Item selection is required')),
          quantity: Yup.number().moreThan(0, t('Quantity must be greater than 0')).required(t('Quantity is required')),
          unit_price: Yup.number().min(0, t('Unit price cannot be negative')).required(t('Unit price is required')),
        })
      )
      .min(1, t('At least one line is required')),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const normalizedLines = values.lines.map((line) => {
        const totals = calculateInvoiceLine(line);
        return {
          item_id: line.item_id || null,
          item_code: line.item_code || null,
          item_name: line.item_name || null,
          quantity: toNumber(line.quantity),
          uom_id: line.uom_id || null,
          unit_price: toNumber(line.unit_price),
          discount1_pct: toNumber(line.discount1_pct),
          discount2_pct: toNumber(line.discount2_pct),
          vat_pct: line.vat_enabled ? (toNumber(line.vat_pct) > 0 ? toNumber(line.vat_pct) : 15) : 0,
          purchase_price: toNumber(line.purchase_price || line.unit_price),
          bonus_qty: toNumber(line.bonus_qty),
          batch_number: line.batch_number || null,
          expiry_date: normalizeDate(line.expiry_date) || null,
          line_total: Number(totals.lineTotal.toFixed(2)),
          payload: {},
        };
      });

      const erpClient = getERPClient();
      const preOrderId = isEdit
        ? (existing?.pre_order_id || null)
        : (values.pre_order_id === 'none' || !values.pre_order_id ? null : values.pre_order_id);
      const payload = {
        invoice_number: values.invoice_number.trim(),
        supplier_id: values.supplier_id,
        pre_order_id: preOrderId,
        invoice_date: normalizeDate(values.invoice_date),
        payment_due_date: normalizeDate(values.payment_due_date),
        payment_type: values.payment_type,
        currency: values.currency.toUpperCase(),
        subtotal: toNumber(values.subtotal),
        discount_total: toNumber(values.discount_total),
        vat_total: toNumber(values.vat_total),
        grand_total: toNumber(values.grand_total),
        total_amount: toNumber(values.grand_total),
        status: values.status,
        payment_terms: values.payment_terms,
        payload: { source: 'frontend', payment_type: values.payment_type },
        lines: normalizedLines,
        items: normalizedLines,
      };

      if (isEdit) {
        await erpClient.updatePurchaseInvoice(id, payload);
        toast.success(t('Purchase Invoice updated successfully'));
      } else {
        await erpClient.createPurchaseInvoice(payload);
        toast.success(t('Purchase Invoice created successfully'));
      }
      navigate('/manager/erp/purchase-invoices');
    } catch (error) {
      console.error('Failed to save purchase invoice:', error);
      const msg = error.response?.data?.message || t('Error saving purchase invoice');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <i className="pi pi-spin pi-spinner text-3xl mb-2" />
        <p>{t('Loading Purchase Invoice...')}</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 max-w-[1600px] mx-auto">
      <PageHeader
        title={isEdit ? t('Edit Purchase Invoice') : t('Create Purchase Invoice')}
        backPath="/manager/erp/purchase-invoices"
      />

      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting, errors }) => {
          const handlePreOrderSelect = async (value) => {
            setFieldValue('pre_order_id', value);
            if (isEdit || value === 'none' || !value) return;
            try {
              const erpClient = getERPClient();
              const res = await erpClient.getPreOrder(value);
              const detail = res.data || res;
              const po = detail?.pre_order || detail?.purchase_order || detail || {};
              const linesArr = detail?.items || detail?.lines || po?.items || po?.lines || [];
              if (po?.supplier_id) setFieldValue('supplier_id', po.supplier_id);
              if (po?.expected_delivery_date || po?.required_date) {
                setFieldValue('payment_due_date', normalizeDate(po.expected_delivery_date || po.required_date));
              }
              if (po?.currency) setFieldValue('currency', po.currency);
              if (po?.payment_terms) setFieldValue('payment_terms', po.payment_terms);
              if (Array.isArray(linesArr) && linesArr.length) {
                const mapped = linesArr.map((line) => ({
                  ...createLine(),
                  item_id: line.item_id || line.material_id || line.item?.id || '',
                  item_code: line.item_code || line.item?.item_code || '',
                  item_name: line.item_name || line.item?.name_en || line.item?.name_ar || line.description || '',
                  quantity: toNumber(line.requested_qty ?? line.quantity ?? line.qty ?? 1),
                  unit_price: toNumber(line.unit_price ?? line.purchase_price ?? line.standard_cost ?? 0),
                  vat_pct: toNumber(line.vat_pct ?? line.vat_rate ?? 15),
                  vat_enabled: toNumber(line.vat_pct ?? line.vat_rate ?? 15) > 0,
                  purchase_price: toNumber(line.unit_price ?? line.purchase_price ?? line.standard_cost ?? 0),
                }));
                setFieldValue('lines', mapped);
                mapped.forEach((mline, mIdx) => {
                  if (mline.item_id) handleItemLevels(mline.item_id, mIdx);
                });
              }
            } catch (err) {
              console.error('Failed to load purchase order:', err);
              toast.error(t('Failed to load purchase order'));
            }
          };

          const handleItemLevels = async (itemId, lineIndex, defaultPrice = 0) => {
            if (!itemId) return;
            try {
              const erpClient = getERPClient();
              const res = await erpClient.getItemPackagingLevels(itemId);
              const levels = Array.isArray(res) ? res : (res?.data || []);
              const active = (levels || []).filter((l) => l.is_active !== false);
              setItemLevels(itemId, buildLevelOptions(active));
              const chosen = active.find((l) => l.is_default_purchase) || active.find((l) => l.is_base_uom) || active[0] || null;
              if (chosen?.uom_id) setFieldValue(`lines.${lineIndex}.uom_id`, chosen.uom_id);
              const levelPrice = chosen && toNumber(chosen.unit_price) > 0 ? toNumber(chosen.unit_price) : 0;
              const price = levelPrice > 0 ? levelPrice : toNumber(defaultPrice);
              if (price > 0) setFieldValue(`lines.${lineIndex}.unit_price`, price);
            } catch (err) {
              console.error('Failed to load item packaging levels:', err);
            }
          };

          const computedLines = values.lines.map(calculateInvoiceLine);
          const subtotal = computedLines.reduce((s, l) => s + l.base, 0);
          const discountTotal = computedLines.reduce((s, l) => s + l.discount1 + l.discount2, 0);
          const vatTotal = computedLines.reduce((s, l) => s + l.vatAmount, 0);
          const grandTotal = computedLines.reduce((s, l) => s + l.lineTotal, 0);

          return (
            <Form className="space-y-4">
              {isEdit && <RestoreItemUOMs existing={existing} onLevels={setItemLevels} />}
              <InvoiceTotalsSync values={values} setFieldValue={setFieldValue} />

              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-5">
                  <SharedInputText label={`${t('Invoice #')} *`} name="invoice_number" placeholder={t('Invoice #')} />
                  <SharedDropdown label={`${t('Supplier')} *`} name="supplier_id" options={supplierOptions} />
                  <SharedDropdown
                    label={t('Purchase Order')}
                    name="pre_order_id"
                    options={preOrderOptions}
                    onChange={(e) => handlePreOrderSelect(e.value)}
                  />
                  <SharedInputText label={`${t('Invoice Date')} *`} name="invoice_date" type="date" />
                  <SharedInputText label={`${t('Payment Deadline')} *`} name="payment_due_date" type="date" />
                  <SharedDropdown
                    label={`${t('Payment Type')} *`}
                    name="payment_type"
                    options={[
                      { value: 'CREDIT', label: t('Credit') },
                      { value: 'CASH', label: t('Cash') },
                      { value: 'BANK_TRANSFER', label: t('Bank Transfer') },
                    ]}
                  />
                  <SharedInputText label={`${t('Payment Terms')} *`} name="payment_terms" placeholder="90 DAYS" />
                  <SharedInputText label={`${t('Currency')} *`} name="currency" placeholder="SAR" />
                  <SharedDropdown
                    label={isEdit ? t('Status') : `${t('Status')} *`}
                    name="status"
                    options={[
                      { value: 'RECEIVED', label: t('Received') },
                      { value: 'PENDING', label: t('Pending') },
                      { value: 'DRAFT', label: t('Draft') },
                    ]}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t('Selecting a Purchase Order auto-fills the supplier and line items.')}
                </p>
              </div>

              <FieldArray name="lines" render={({ push, remove }) => (
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex justify-between items-center border-b pb-2 mb-2">
                    <h3 className="font-semibold text-gray-800 text-sm">{t('Items')}</h3>
                    <SharedButton
                      type="button"
                      label={t('Add Another Item')}
                      buttonType="primary"
                      icon="pi pi-plus"
                      onClick={() => push(createLine())}
                    />
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-gray-100 text-gray-600">
                          <th className="p-2 w-0" />
                          <th className="p-2 min-w-[180px] text-start">{t('Item')}</th>
                          <th className="p-2 min-w-[90px] text-start">{t('Qty')}</th>
                          <th className="p-2 min-w-[120px] text-start">{t('UOM')}</th>
                          <th className="p-2 min-w-[100px] text-start">{t('Unit Price')}</th>
                          <th className="p-2 min-w-[70px] text-start">{t('Disc 1 %')}</th>
                          <th className="p-2 min-w-[70px] text-start">{t('Disc 2 %')}</th>
                          <th className="p-2 min-w-[100px] text-start">{t('Batch No')}</th>
                          <th className="p-2 min-w-[110px] text-start">{t('Expire Date')}</th>
                          <th className="p-2 min-w-[50px] text-center">{t('VAT')}</th>
                          <th className="p-2 min-w-[90px] text-start">{t('Purchase Price')}</th>
                          <th className="p-2 min-w-[70px] text-start">{t('Bonus')}</th>
                          <th className="p-2 min-w-[80px] text-end">{t('Total')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {values.lines.map((line, index) => {
                          const totals = computedLines[index] || { lineTotal: 0 };
                          const lineUomOptions = itemUomOptions[line.item_id];
                          const uomSelectOptions = (lineUomOptions && lineUomOptions.length ? lineUomOptions : uomOptions);
                          return (
                            <tr key={index} className="border bg-gray-50">
                              <td className="p-2 align-top text-center">
                                <DeleteIcon
                                  onClick={() => remove(index)}
                                  className={values.lines.length === 1 ? 'opacity-30 pointer-events-none' : ''}
                                />
                              </td>
                              <td className="p-2 align-top">
                                <ERPItemSearchField
                                  name={line.item_id}
                                  items={items}
                                  isLoading={false}
                                  fallbackLabel={
                                    [line.item_code, line.item_name].filter(Boolean).join(' - ')
                                  }
                                  onSelect={async (item) => {
                                    const itemId = getItemId(item);
                                    setFieldValue(`lines.${index}.item_id`, itemId);
                                    setFieldValue(`lines.${index}.item_code`, item.item_code || '');
                                    setFieldValue(`lines.${index}.item_name`, item.name_en || item.name_ar || item.name || '');
                                    if (item.stock_uom_id) setFieldValue(`lines.${index}.uom_id`, item.stock_uom_id);
                                    await handleItemLevels(itemId, index, toNumber(item.standard_cost || item.unit_price || 0));
                                  }}
                                />
                              </td>
                              <td className="p-2 align-top">
                                <input
                                  type="number"
                                  min="1"
                                  value={line.quantity}
                                  onChange={(e) => setFieldValue(`lines.${index}.quantity`, e.target.value)}
                                  className="w-full border border-gray-300 rounded p-1.5 text-xs focus:outline-none h-[30px]"
                                />
                              </td>
                              <td className="p-2 align-top">
                                <select
                                  value={line.uom_id}
                                  onChange={(e) => setFieldValue(`lines.${index}.uom_id`, e.target.value)}
                                  className="w-full border border-gray-300 rounded p-1.5 text-xs focus:outline-none h-[30px]"
                                >
                                  <option value="">{t('Select UOM')}</option>
                                  {uomSelectOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="p-2 align-top">
                                <input
                                  type="number"
                                  min="0"
                                  step="any"
                                  value={line.unit_price}
                                  onChange={(e) => setFieldValue(`lines.${index}.unit_price`, e.target.value)}
                                  className="w-full border border-gray-300 rounded p-1.5 text-xs focus:outline-none h-[30px]"
                                />
                              </td>
                              <td className="p-2 align-top">
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={line.discount1_pct}
                                  onChange={(e) => setFieldValue(`lines.${index}.discount1_pct`, e.target.value)}
                                  className="w-full border border-gray-300 rounded p-1.5 text-xs focus:outline-none h-[30px]"
                                />
                              </td>
                              <td className="p-2 align-top">
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={line.discount2_pct}
                                  onChange={(e) => setFieldValue(`lines.${index}.discount2_pct`, e.target.value)}
                                  className="w-full border border-gray-300 rounded p-1.5 text-xs focus:outline-none h-[30px]"
                                />
                              </td>
                              <td className="p-2 align-top">
                                <input
                                  type="text"
                                  value={line.batch_number || ''}
                                  placeholder="B-2026-08"
                                  onChange={(e) => setFieldValue(`lines.${index}.batch_number`, e.target.value)}
                                  className="w-full border border-gray-300 rounded p-1.5 text-xs focus:outline-none h-[30px]"
                                />
                              </td>
                              <td className="p-2 align-top">
                                <input
                                  type="date"
                                  value={line.expiry_date || ''}
                                  onChange={(e) => setFieldValue(`lines.${index}.expiry_date`, e.target.value)}
                                  className="w-full border border-gray-300 rounded p-1.5 text-xs focus:outline-none h-[30px]"
                                />
                              </td>
                              <td className="p-2 align-top text-center">
                                <input
                                  type="checkbox"
                                  checked={line.vat_enabled}
                                  title="15% VAT"
                                  onChange={(e) => setFieldValue(`lines.${index}.vat_enabled`, e.target.checked)}
                                />
                              </td>
                              <td className="p-2 align-top">
                                <input
                                  type="number"
                                  min="0"
                                  step="any"
                                  readOnly
                                  value={Number(calculateInvoiceLine(line).purchasePrice || 0).toFixed(2)}
                                  className="w-full border border-gray-300 rounded p-1.5 text-xs focus:outline-none h-[30px] bg-gray-100"
                                />
                              </td>
                              <td className="p-2 align-top">
                                <input
                                  type="number"
                                  min="0"
                                  value={line.bonus_qty}
                                  onChange={(e) => setFieldValue(`lines.${index}.bonus_qty`, e.target.value)}
                                  className="w-full border border-gray-300 rounded p-1.5 text-xs focus:outline-none h-[30px]"
                                />
                              </td>
                              <td className="p-2 align-top text-end whitespace-nowrap">
                                <span className="font-semibold text-primary">
                                  {totals.lineTotal.toFixed(2)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )} />

              {errors && Object.keys(errors).length > 0 && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-xs rounded-md p-2">
                  {t('Please fix the highlighted fields before saving.')}
                </div>
              )}

              <div className="flex justify-end">
                <div className="w-full max-w-sm bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>{t('Subtotal:')}</span>
                      <span>{subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-red-500">
                      <span>{t('Total Discount:')}</span>
                      <span>{discountTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-emerald-600">
                      <span>{t('Total VAT (15%):')}</span>
                      <span>{vatTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2 text-base font-bold">
                      <span>{t('Final Total:')}</span>
                      <span className="text-primary">{grandTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                <SharedButton
                  type="button"
                  label={t('Cancel')}
                  icon="pi pi-times"
                  buttonType="reset"
                  onClick={() => navigate('/manager/erp/purchase-invoices')}
                />
                <SharedButton
                  type="submit"
                  label={isSubmitting ? t('Saving...') : isEdit ? t('Update') : t('Submit Purchase')}
                  icon="pi pi-check"
                  buttonType="primary"
                  loading={isSubmitting}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}